/**
 * "You are looking at an old version" — the visible half of #2246.
 *
 * It OFFERS a reload and never performs one: an operator can be halfway
 * through a dispatch form when a deploy lands, and losing that work to an
 * automatic refresh would be a worse bug than the one this fixes. Once shown
 * it stays shown, because the only thing that makes it untrue is the click.
 *
 * Plain Tailwind on a native button, following `PrivacyBanner` — this app has
 * no shadcn button primitive of its own, and #2246 is not the place to add one.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useBuildVersionCheck } from '@hooks/useBuildVersionCheck';

interface NewBuildBannerProps {
  /**
   * Injected only so tests can assert that the click — and nothing else —
   * triggers the reload; production always takes the default.
   */
  readonly onReload?: () => void;
}

export const NewBuildBanner: React.FC<NewBuildBannerProps> = ({ onReload }) => {
  const { t } = useTranslation();
  const newBuildAvailable = useBuildVersionCheck();

  if (!newBuildAvailable) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center justify-center gap-3 border-b border-border bg-accent/10 px-4 py-2 text-sm text-foreground"
    >
      <span>{t('app.newVersion.message')}</span>
      <button
        type="button"
        onClick={() => (onReload ? onReload() : window.location.reload())}
        className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
      >
        {t('app.newVersion.reload')}
      </button>
    </div>
  );
};

export default NewBuildBanner;
